import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import {
	applyClassroomRulesList,
	clearClassroomRules,
	createInClassRule,
	CreateInClassRuleRequest,
	deleteInClassRule,
	getClassroomRules,
	GetClassroomRulesResponse,
	PatchInClassRuleRequest,
	removeClassroomRulesList,
	updateInClassRule,
} from "@/api/classroomRules";
import { AppControlClassroomRef, deleteAppControls, NativeAppControlRule } from "@/api/classroomAppControls";
import { classroomAppControlsQueryKey } from "./classroomAppControlsQuery";

export const classroomRulesQueryRootKey = ["classroomRules"] as const;

export function classroomRulesQueryKey(classroomId: string): readonly [string, string] {
	return ["classroomRules", classroomId] as const;
}

const EMPTY_CLASSROOM_RULES: GetClassroomRulesResponse = { appliedLists: [], inClassRules: [] };

export function useClassroomRulesQuery(
	applianceId: string,
	classroomId: string,
	enabled = true
): UseQueryResult<GetClassroomRulesResponse> {
	return useQuery<GetClassroomRulesResponse, Error>({
		queryKey: classroomRulesQueryKey(classroomId),
		queryFn: () => getClassroomRules(applianceId, classroomId),
		enabled: enabled && Boolean(applianceId) && Boolean(classroomId),
		retry: false,
	});
}

/** Ticket taken by the next write, and the newest ticket whose response reached the cache. */
let nextWriteTicket = 0;
const appliedWriteTickets = new Map<string, number>();

type ClassroomRulesWriteContext = { ticket: number };

function takeWriteTicket(): ClassroomRulesWriteContext {
	nextWriteTicket += 1;
	return { ticket: nextWriteTicket };
}

/** True when this ticket is the newest to reach the cache and may write it. */
function claimWriteTicket(classroomId: string, ticket: number): boolean {
	if (ticket < (appliedWriteTickets.get(classroomId) ?? 0)) {
		return false;
	}

	appliedWriteTickets.set(classroomId, ticket);
	return true;
}

/**
 * Classroom-rules writes other than clear respond with the whole updated state, so the cache is
 * replaced from the response rather than patched or invalidated. Clear responds 204, so it empties
 * the cache instead.
 *
 * Active Rules keeps rows other than the one being saved clickable, so two writes (including clear)
 * can be in flight at once and their responses can arrive in either order. Each write takes a
 * ticket, and a response that predates the state already in the cache is refetched instead of
 * written, because writing it would undo the newer change.
 */
function useClassroomRulesWrite<TVariables>(
	classroomId: string,
	mutationFn: (variables: TVariables) => Promise<GetClassroomRulesResponse>
): UseMutationResult<GetClassroomRulesResponse, Error, TVariables> {
	const queryClient = useQueryClient();

	return useMutation<GetClassroomRulesResponse, Error, TVariables, ClassroomRulesWriteContext>({
		mutationFn,
		onMutate: () => takeWriteTicket(),
		onSuccess: (updated, _variables, context) => {
			const ticket = context?.ticket ?? 0;
			if (!claimWriteTicket(classroomId, ticket)) {
				void queryClient.invalidateQueries({ queryKey: classroomRulesQueryKey(classroomId) });
				return;
			}

			queryClient.setQueryData<GetClassroomRulesResponse>(classroomRulesQueryKey(classroomId), updated);
		},
	});
}

export function useApplyClassroomRulesListMutation(
	applianceId: string,
	classroomId: string
): UseMutationResult<GetClassroomRulesResponse, Error, string> {
	return useClassroomRulesWrite<string>(classroomId, (listId) => applyClassroomRulesList(applianceId, classroomId, listId));
}

export function useRemoveClassroomRulesListMutation(
	applianceId: string,
	classroomId: string
): UseMutationResult<GetClassroomRulesResponse, Error, string> {
	return useClassroomRulesWrite<string>(classroomId, (listId) => removeClassroomRulesList(applianceId, classroomId, listId));
}

export function useCreateInClassRuleMutation(
	applianceId: string,
	classroomId: string
): UseMutationResult<GetClassroomRulesResponse, Error, CreateInClassRuleRequest> {
	return useClassroomRulesWrite<CreateInClassRuleRequest>(classroomId, (body) => createInClassRule(applianceId, classroomId, body));
}

export type UpdateInClassRuleVariables = {
	ruleId: string;
	body: PatchInClassRuleRequest;
};

export function useUpdateInClassRuleMutation(
	applianceId: string,
	classroomId: string
): UseMutationResult<GetClassroomRulesResponse, Error, UpdateInClassRuleVariables> {
	return useClassroomRulesWrite<UpdateInClassRuleVariables>(classroomId, ({ ruleId, body }) =>
		updateInClassRule(applianceId, classroomId, ruleId, body)
	);
}

export function useDeleteInClassRuleMutation(
	applianceId: string,
	classroomId: string
): UseMutationResult<GetClassroomRulesResponse, Error, string> {
	return useClassroomRulesWrite<string>(classroomId, (ruleId) => deleteInClassRule(applianceId, classroomId, ruleId));
}

export type ClearActiveRulesVariables = {
	/** App-control rows to delete, since the clear endpoint does not touch app controls. */
	appControlRowIds: string[];
};

/**
 * Clearing Active Rules spans two stores, so both requests run together and neither cache is
 * emptied until they have all finished — including when one fails. That keeps the panel from
 * refetching mid-flight and showing a mix of deleted and remaining rows. Clear responds 204 with
 * no body, so a full success empties the caches rather than replacing them from a response.
 */
export function useClearActiveRulesMutation(
	applianceId: string,
	classroomId: string,
	appControlClassroom: AppControlClassroomRef
): UseMutationResult<void, Error, ClearActiveRulesVariables> {
	const queryClient = useQueryClient();

	return useMutation<void, Error, ClearActiveRulesVariables, ClassroomRulesWriteContext>({
		mutationFn: async ({ appControlRowIds }) => {
			const results = await Promise.allSettled([
				clearClassroomRules(applianceId, classroomId),
				appControlRowIds.length > 0 ? deleteAppControls(appControlClassroom, appControlRowIds) : Promise.resolve(),
			]);
			const rejected = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
			if (rejected !== undefined) {
				throw rejected.reason instanceof Error ? rejected.reason : new Error(String(rejected.reason));
			}
		},
		onMutate: () => takeWriteTicket(),
		onSuccess: (_result, { appControlRowIds }, context) => {
			const ticket = context?.ticket ?? 0;
			if (!claimWriteTicket(classroomId, ticket)) {
				void queryClient.invalidateQueries({ queryKey: classroomRulesQueryKey(classroomId) });
				void queryClient.invalidateQueries({ queryKey: classroomAppControlsQueryKey(appControlClassroom.classroomStableIdV1) });
				return;
			}

			queryClient.setQueryData<GetClassroomRulesResponse>(classroomRulesQueryKey(classroomId), EMPTY_CLASSROOM_RULES);
			queryClient.setQueryData<NativeAppControlRule[]>(classroomAppControlsQueryKey(appControlClassroom.classroomStableIdV1), (rows) =>
				(rows ?? []).filter((row) => !appControlRowIds.includes(row.id))
			);
		},
		onError: (_error, _variables, context) => {
			const ticket = context?.ticket ?? 0;
			claimWriteTicket(classroomId, ticket);
			// One request can fail while the other succeeds, so both are refetched to show what is left.
			void queryClient.invalidateQueries({ queryKey: classroomRulesQueryKey(classroomId) });
			void queryClient.invalidateQueries({ queryKey: classroomAppControlsQueryKey(appControlClassroom.classroomStableIdV1) });
		},
	});
}
